<template>
    <vx-table-row class="outline outline-2 outline-offset-4 outline-amber-500">
        <vx-table-row-title class="font-normal!">
            <select-org-unit
                v-if="props.type === 'ou'"
                ref="targetSelectRef"
                v-model="selectedTarget"
                :teamId="props.teamId"
                :clearable="false"
                data-test="target-select"
            />
            <select-group
                v-else
                ref="targetSelectRef"
                v-model="selectedTarget"
                :teamId="props.teamId"
                :reduce="(option) => option"
                :clearable="false"
                data-test="target-select"
            />
        </vx-table-row-title>

        <vx-table-row-item class="min-w-[100px]">
            <template v-slot:label> Type: </template>
            <vx-badge v-if="props.type === 'ou'" size="xs" color="violet" data-test="type-badge">
                <font-awesome-icon :icon="faBuildingFlag"></font-awesome-icon>
                OU
            </vx-badge>
            <vx-badge v-else size="xs" color="slate" data-test="type-badge">
                <font-awesome-icon :icon="faGrid2Plus" class="-mx-0.5"></font-awesome-icon>
                Group
            </vx-badge>
        </vx-table-row-item>

        <vx-table-row-item class="min-w-[220px]">
            <template v-slot:label>
                <span class="inline-flex items-center gap-1">
                    Role:
                    <group-role-permissions-popover v-if="props.type === 'group'" />
                </span>
            </template>
            <vx-selectable
                v-model="selectedRole"
                :reduce="(option) => option.value"
                :options="roleOptions"
                :clearable="false"
                :disabled="saving"
                data-test="role-select"
                class="w-[200px]"
            />
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-button
                data-test="cancel-add"
                aria-label="Cancel"
                @click="emit('cancel')"
                type="button"
                color="muted"
                outline
            >
                <font-awesome-icon :icon="faCircleX"></font-awesome-icon>
            </vx-button>
            <vx-button
                data-test="confirm-add"
                @click="save"
                type="button"
                color="primary"
                :loading="saving"
                :disabled="!canSave"
                class="w-[125px]"
            >
                Add Target
                <font-awesome-icon widthAuto :icon="faCirclePlus" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup>
import { faBuildingFlag, faCirclePlus, faCircleX, faGrid2Plus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxSelectable,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { orgUnitUsersClient, groupUsersClient } from '~/services';
import { orgUnitRolesOptions, groupRolesOptions, DEFAULT_ORG_UNIT_ROLE, DEFAULT_GROUP_ROLE } from '~/constants/roles';
import { catchRequestError } from '~/utils/request';
import SelectOrgUnit from '~/components/general/SelectOrgUnit.vue';
import SelectGroup from '~/components/general/SelectGroup.vue';
import GroupRolePermissionsPopover from '~/components/settings/teams/groups/GroupRolePermissionsPopover.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    userId: {
        type: Number,
        required: true,
    },
    type: {
        type: String,
        required: true,
        validator: (val) => ['ou', 'group'].includes(val),
    },
});

const emit = defineEmits(['saved', 'cancel']);

const toasted = useToasted();
const saving = ref(false);

const targetSelectRef = ref(null);
const selectedTarget = ref(null);
const selectedRole = ref(props.type === 'ou' ? DEFAULT_ORG_UNIT_ROLE : DEFAULT_GROUP_ROLE);

const roleOptions = computed(() => (props.type === 'ou' ? orgUnitRolesOptions() : groupRolesOptions()));

const getTargetId = (target) => {
    if (props.type === 'ou') {
        return target?.id;
    }
    return target?.value || target?.group_id;
};

const getTargetName = (target) => {
    if (props.type === 'ou') {
        return target?.name;
    }
    return target?.name || '';
};

const canSave = computed(() => {
    return !!getTargetId(selectedTarget.value) && !!selectedRole.value && !saving.value;
});

const save = async () => {
    const targetId = getTargetId(selectedTarget.value);
    const role = selectedRole.value;

    if (!targetId || !role || saving.value) {
        return;
    }

    saving.value = true;

    try {
        if (props.type === 'ou') {
            await orgUnitUsersClient.upsertOrgUnitUser(props.teamId, targetId, props.userId, { role });
        } else {
            await groupUsersClient.upsertGroupUser(props.teamId, targetId, props.userId, { role });
        }

        const name = getTargetName(selectedTarget.value);

        emit('saved', {
            targetId,
            name,
            type: props.type,
            role,
        });
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        saving.value = false;
    }
};

onMounted(async () => {
    await nextTick();
    targetSelectRef.value?.$el?.querySelector('input')?.focus();
});
</script>
